import { Button, Flex, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Text, useToast } from '@chakra-ui/react'
import { organizationListContext } from 'BrokerTsLib/src/context'
import type { OrganizationUnitGridItem, OrganizationUnitPagedQuery } from 'BrokerTsLib/src/dtos'
import { useGetAll, useTransferUser } from 'BrokerTsLib/src/hooks/useOrganizationUnit'
import { useState } from 'react'

import { Pagination } from '@/containers/Pagination'

import { ArrowLeft } from '../../icons'
import { OrganizationListCityColumn, OrganizationListDescriptionColumn, OrganizationListNameColumn, OrganizationListTypeColumn, OrganizationListValidColumn, OrganizationsList } from '../organization/OrganizationList'
import { OrganizationListFilter } from '../organization/OrganizationListFilter'

type UserTransferModalProps = {
  userIds: string[]
  organizationUnitId?: number
  isOpen: boolean
  onClose: () => void
  transferSuccessCallback?: () => void
  transferFailureCallback?: () => void
}

const pageSize = 15
export const UserTransferModal = ({
  userIds,
  organizationUnitId,
  isOpen,
  onClose,
  transferSuccessCallback,
  transferFailureCallback
}: UserTransferModalProps) => {
  const toast = useToast()

  const [filter, setFilter] = useState<OrganizationUnitPagedQuery>({
    showInvalid: false,
    showRootOnly: false,
    pageIndex: 1,
    pageSize
  })

  const { data, refetch: refetchOrganizations, isLoading } = useGetAll(filter, { enabled: isOpen })
  const { mutateAsync: transferUser, isLoading: isTransferUserLoading } = useTransferUser()

  const possibleOrganizations = data?.data.items.filter(x => !organizationUnitId || x.id !== organizationUnitId) ?? []

  const handleTransfer = (organization: OrganizationUnitGridItem) => {
    transferUser({ userIds, organizationUnitId: organization.id })
      .then(() => {
        const text = userIds.length > 1
          ? 'Uživatelé byli přesunuti do organizace'
          : 'Uživatel byl přesunut do organizace'

        toast({
          title: `${text} ${organization.name}.`,
          status: 'success'
        })

        transferSuccessCallback?.()
      })
      .catch(() => {
        toast({
          title: `Chyba při přesunu do organizace ${organization.name}.`,
          status: 'error'
        })

        transferFailureCallback?.()
      })
      .finally(() => {
        onClose()
      })
  }

  return (
    <>
      <Modal size="6xl" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />

        <ModalContent>
          <ModalHeader>
            <Text>Přesunout do jiné organizace</Text>
            <ModalCloseButton />
          </ModalHeader>

          <ModalBody sx={{ pt: 0, pb: 6, gap: 4 }}>
            <organizationListContext.Provider value={{
              loading: isLoading,
              totalCount: data?.data.totalCount ?? 0,
              currentPage: filter.pageIndex,
              pageSize,
              filter,
              setCurrentPage: (p: number) => setFilter(f => ({ ...f, pageIndex: p })),
              setPageSize: () => { },
              updateFilter: setFilter,
              reload: () => refetchOrganizations()
            }}>
              <Flex flexDir={'column'} gap={4}>
                <OrganizationListFilter />
                <OrganizationsList organizations={possibleOrganizations} sx={{ boxShadow: 'none' }}
                  columnsDefinition={[
                    OrganizationListNameColumn,
                    OrganizationListDescriptionColumn,
                    OrganizationListCityColumn,
                    OrganizationListValidColumn,
                    OrganizationListTypeColumn,
                    {
                      key: 'actions', name: '', cellExpr: (item) =>
                        <Button
                          size={'sm'}
                          colorScheme={'primary'}
                          onClick={() => handleTransfer(item)}
                          isLoading={isTransferUserLoading}
                          isDisabled={isTransferUserLoading}
                        >
                          <ArrowLeft boxSize={'14px'} />
                          Přesunout
                        </Button>
                    }
                  ]}
                />
                <Pagination context={organizationListContext} />
              </Flex>
            </organizationListContext.Provider>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}